import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import {
    LaboratoryProcessConfigurationCreateRequest, LaboratoryProcessConfigurationPatchInBatchRequest,
    LaboratoryProcessConfigurationResponse
} from '@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/services/laboratory-process/laboratory-process.dto'
import {
    initialLaboratoryProcessGridState,
    LaboratoryProcessGridState
} from '@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/components/grid/laboratory-process-grid.model'
import LaboratoryProcessModal
    from '@/modules/configurationModule/detail/local-laboratory-module/laboratory-process/components/modal/laboratory-process-modal.vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        GridDisplayOptions,
        VtGrid,
        LaboratoryProcessModal,
        CodificationSearcherModal
    }
})
export default class LaboratoryProcessGrid extends BaseCrudServerSideComponent<LaboratoryProcessConfigurationResponse> implements BaseCrud {
    @Ref() readonly laboratoryProcessModalWrapper!: typeof ModalWrapper

    state: LaboratoryProcessGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialLaboratoryProcessGridState()
    }

    /// SERVICES CALLS ///

    private async _createLaboratoryProcess(createRequest: LaboratoryProcessConfigurationCreateRequest): Promise<LaboratoryProcessConfigurationResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchLaboratoryProcesses(batchRequests: LaboratoryProcessConfigurationPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteLaboratoryProcesses(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: LaboratoryProcessConfigurationCreateRequest): Promise<void> {
        if (createRequest) {
            const section = await this._createLaboratoryProcess(createRequest)
            if (section) {
                this.reloadData()
                this.onUpdate(section)
            }
        }
    }

    async patchOperation(batchRequest: LaboratoryProcessConfigurationPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchLaboratoryProcesses([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteLaboratoryProcesses(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(section: LaboratoryProcessConfigurationResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (section) {
            this.showModal(this.$tc('configuration.actions.update'), section, this.laboratoryProcessModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(section => section.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.laboratoryProcessModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
